import { dpFor, fromBase, toBase } from "./convert";
import { findUnit, type Quantity, tryFindUnit, UNITS, type UnitDefinition } from "./registry";

export type FieldUnitSpec = {
  quantity: Quantity;
  canonical: string;
  // Units the field may switch to, beyond the canonical. Omitted means it does not switch: a
  // canonical pairs with one counterpart (mm↔in, m↔ft) rather than with a whole system, and the dp a
  // converted unit derives is a field decision, so there is no default that is right unasked.
  switchable?: readonly string[];
  dp?: number;
};

// Computed values: `unit` is the editor's chosen display unit, `si` the untouched result.
export type SiValue = { si: number; unit: string };

// Entered values: `unit` is the unit `value` was typed in, `si` the same amount in the field's canonical unit.
export type Measure = SiValue & { value: number };

export const isMeasure = (value: SiValue): value is Measure => "value" in value && typeof value.value === "number";

// Tells a widened value from a bare one without a spec, for the readers that accept either while
// documents written before the widening are still in circulation. `si` carries the unit the field's
// canonical declares, so a reader that only needs the number needs nothing else.
export const isSiValue = (value: unknown): value is SiValue =>
  typeof value === "object" && value !== null && typeof (value as SiValue).si === "number";

export const finiteSi = (value: SiValue | null | undefined): number | null =>
  value != null && Number.isFinite(value.si) ? value.si : null;

export const makeMeasure = (value: number, unitId: string, spec: FieldUnitSpec): Measure => {
  const canonical = findUnit(spec.quantity, spec.canonical);
  return {
    value,
    unit: unitId,
    si: unitId === canonical.id ? value : fromBase(canonical, toBase(findUnit(spec.quantity, unitId), value)),
  };
};

export const resolveIn = (value: SiValue, spec: FieldUnitSpec, targetUnitId: string): number => {
  if (isMeasure(value) && targetUnitId === value.unit) return value.value;
  const canonical = findUnit(spec.quantity, spec.canonical);
  return targetUnitId === canonical.id
    ? value.si
    : fromBase(findUnit(spec.quantity, targetUnitId), toBase(canonical, value.si));
};

export const measureIn = (measure: Measure, spec: FieldUnitSpec, targetUnitId: string): number =>
  resolveIn(measure, spec, targetUnitId);

export const asMeasure = (value: number | Measure, spec: FieldUnitSpec): Measure =>
  typeof value === "number" ? { value, unit: findUnit(spec.quantity, spec.canonical).id, si: value } : value;

export const asSiValue = (value: number | SiValue, spec: FieldUnitSpec): SiValue =>
  typeof value === "number" ? { si: value, unit: findUnit(spec.quantity, spec.canonical).id } : value;

export const asNullableMeasure = (value: number | Measure | null | undefined, spec: FieldUnitSpec): Measure | null =>
  value == null ? null : asMeasure(value, spec);

// A contract as it sits in a stored document, where any widened field may still be the bare number a
// pre-widening write left there. Read boundaries take this and hand back the contract itself.
export type Stored<T> = {
  [K in keyof T]: T[K] extends Measure | null
    ? number | Measure | null
    : T[K] extends SiValue | null
      ? number | SiValue | null
      : T[K] extends (Measure | null)[]
        ? (number | Measure | null)[]
        : T[K];
};

export const asNullableSiValue = (value: number | SiValue | null | undefined, spec: FieldUnitSpec): SiValue | null =>
  value == null ? null : asSiValue(value, spec);

// The one place a unit id read back from a document becomes safe to pass to findUnit/formatMeasure.
export const resolveUnitId = (spec: FieldUnitSpec, unitId?: string): string =>
  tryFindUnit(spec.quantity, unitId ?? spec.canonical)?.id ?? spec.canonical;

export const switchableUnits = (spec: FieldUnitSpec): readonly UnitDefinition[] => {
  const canonical = findUnit(spec.quantity, spec.canonical);
  const alternatives = (spec.switchable ?? []).map((id) => findUnit(spec.quantity, id));
  return [canonical, ...alternatives.filter((unit) => unit.id !== canonical.id)];
};

export const dpForSpec = (spec: FieldUnitSpec, unitId: string): number =>
  dpFor(
    findUnit(spec.quantity, unitId),
    findUnit(spec.quantity, spec.canonical),
    spec.dp ?? UNITS[spec.quantity].decimalPlaces,
  );
